import { AddSchemaPropertyDialog } from "#src/batteries-included-components/Dialogs/AddSchemaPropertyDialog/AddSchemaPropertyDialog";
import { useListWorkerVersions } from "#src/components/hooks/adapters/useWorkers";
import { useMultiStepFormContext } from "#src/hooks/useMultiStepForm";
import { displayValueByDataType } from "#src/utils/display";
import {
  Accordion,
  Button,
  Dialog,
  EmptyState,
  Form,
  KeyValueList,
  KeyValuePairList,
  Panel,
  useForm,
} from "@validereinc/common-components";
import {
  AttributeDataType,
  AttributeSchemaHelpers,
  InputOutputSchemaPropertyConfigurationType,
  SortDirection,
} from "@validereinc/domain";
import { toTitleCaseString } from "@validereinc/utilities";
import classNames from "classnames/bind";
import React, { useEffect, useMemo, useState } from "react";
import styles from "./CreateWorkerVersionFormPanels.module.scss";

const { AccordionPanel } = Accordion;
const cx = classNames.bind(styles);

const CONFIGURATION_DISPLAY_ORDER: Array<
  keyof InputOutputSchemaPropertyConfigurationType
> = [
  "display_name",
  "data_type",
  "is_required",
  "description",
  "lookup_entity_type",
  "date_resolution",
  "pick_list_values",
];

const isLookupDataType = (dataType: string | undefined): boolean =>
  dataType === AttributeDataType.LOOKUP ||
  dataType === AttributeDataType.MULTI_LOOKUP;

const isPicklistDataType = (dataType: string | undefined): boolean =>
  dataType === AttributeDataType.PICK_LIST ||
  dataType === AttributeDataType.MULTI_PICK_LIST;

const isDateDataType = (dataType: string | undefined): boolean =>
  dataType === AttributeDataType.DATE;

const configurationToKeyValueList = (
  configuration?: InputOutputSchemaPropertyConfigurationType
): KeyValuePairList => {
  if (!configuration || typeof configuration !== "object") return [];

  const dataType = configuration.data_type;
  const propertiesToShow = CONFIGURATION_DISPLAY_ORDER.filter((property) => {
    if (property === "lookup_entity_type") {
      return isLookupDataType(dataType);
    }
    if (property === "pick_list_values") {
      return isPicklistDataType(dataType);
    }
    if (property === "date_resolution") {
      return isDateDataType(dataType);
    }
    return true;
  });

  return propertiesToShow.reduce<KeyValuePairList>((acc, property) => {
    const value = configuration[property];

    let displayValue: React.ReactNode;
    if (property === "data_type") {
      displayValue =
        configuration.data_type != null
          ? displayValueByDataType(
              AttributeSchemaHelpers.getDataTypeForDisplay(
                configuration.data_type
              ),
              "string",
              { alignment: "right" }
            )
          : null;
    } else if (property === "is_required") {
      displayValue = displayValueByDataType(
        configuration.is_required ?? false,
        "boolean",
        { alignment: "right" }
      );
    } else if (property === "pick_list_values") {
      displayValue = displayValueByDataType(value ?? [], "multi-pick-list", {
        alignment: "right",
      });
    } else {
      displayValue = displayValueByDataType(value ?? null, "string", {
        alignment: "right",
      });
    }

    if (property !== "display_name")
      acc.push({
        title: toTitleCaseString(property, { intent: "ui" }),
        value: displayValue,
      });
    return acc;
  }, []);
};

export const InputOutputSchemaPropertyConfiguration = ({
  configuration,
  propertyKey,
  ...keyValueListProps
}: {
  configuration: InputOutputSchemaPropertyConfigurationType;
  propertyKey: string;
} & React.ComponentProps<typeof KeyValueList>) => {
  const data = useMemo(
    () => [
      {
        title: "Machine Name",
        value: propertyKey,
      },
      ...configurationToKeyValueList(configuration),
    ],
    [configuration, propertyKey]
  );

  return (
    <KeyValueList
      data={data}
      variant="shaded"
      color="neutral"
      {...keyValueListProps}
    />
  );
};

export const SchemaType = {
  INPUT: "input",
  OUTPUT: "output",
} as const;
export type SchemaTypeType = (typeof SchemaType)[keyof typeof SchemaType];

type AddSchemaPropertyDialogState =
  | null
  | { mode: "add" }
  | {
      mode: "edit";
      propertyKey: string;
      config: InputOutputSchemaPropertyConfigurationType;
    };

const WorkerVersionInputOutputSchemaFormStep = ({
  workerId,
  stepNumber,
  schemaType,
}: {
  workerId: string;
  stepNumber: number;
  schemaType: SchemaTypeType;
}) => {
  const { currentStep, updateStep } = useMultiStepFormContext();

  const { data: workerVersionsData, isLoading: isLoadingWorkerVersions } =
    useListWorkerVersions(
      {
        meta: { id: workerId },
        filters: {},
        sortBy: "version",
        sortDirection: SortDirection.DESCENDING,
      },
      { enabled: Boolean(workerId) }
    );

  const latestWorkerVersion = useMemo(() => {
    const versions = workerVersionsData?.data ?? [];
    return versions[0];
  }, [workerVersionsData?.data]);

  const schemaKey =
    schemaType === SchemaType.INPUT ? "input_schema" : "output_schema";

  const form = useForm<{
    input_schema?: Record<string, InputOutputSchemaPropertyConfigurationType>;
    output_schema?: Record<string, InputOutputSchemaPropertyConfigurationType>;
  }>({
    defaultValues: {
      input_schema: {},
      output_schema: {},
    },
  });

  const formValues = form.watch();
  const schema = formValues[schemaKey] ?? {};
  const schemaEntries = Object.entries(schema);
  const [addSchemaPropertyDialogState, setAddSchemaPropertyDialogState] =
    useState<AddSchemaPropertyDialogState>(null);
  const isAddSchemaPropertyDialogOpen = addSchemaPropertyDialogState !== null;
  const editingProperty =
    addSchemaPropertyDialogState?.mode === "edit"
      ? {
          propertyKey: addSchemaPropertyDialogState.propertyKey,
          config: addSchemaPropertyDialogState.config,
        }
      : null;

  const [isImportConfirmationDialogOpen, setIsImportConfirmationDialogOpen] =
    useState(false);

  const handleImportFromLatestVersion = () => {
    if (!latestWorkerVersion) return;

    const sourceSchema =
      schemaType === SchemaType.INPUT
        ? latestWorkerVersion.input_schema
        : latestWorkerVersion.output_schema;

    form.setValue(schemaKey, sourceSchema ?? {});
    setIsImportConfirmationDialogOpen(false);
  };

  const handleDeleteProperty = (propertyKey: string) => {
    const currentSchema = { ...schema };
    delete currentSchema[propertyKey];
    form.setValue(schemaKey, currentSchema);
  };

  const handleAddProperty = (
    propertyKey: string,
    config: InputOutputSchemaPropertyConfigurationType
  ) => {
    const newSchema = { ...schema };
    if (editingProperty) {
      delete newSchema[editingProperty.propertyKey];
    }
    newSchema[propertyKey] = config;
    form.setValue(schemaKey, newSchema);
    setAddSchemaPropertyDialogState(null);
  };

  const handleEditProperty = (propertyKey: string) => {
    const config = schema[propertyKey];
    if (config) {
      setAddSchemaPropertyDialogState({ mode: "edit", propertyKey, config });
    }
  };

  useEffect(() => {
    if (latestWorkerVersion) {
      const sourceSchema =
        schemaType === SchemaType.INPUT
          ? latestWorkerVersion.input_schema
          : latestWorkerVersion.output_schema;
      form.setValue(schemaKey, sourceSchema ?? {});
    }
  }, [latestWorkerVersion, schemaType, schemaKey]);

  useEffect(() => {
    updateStep(stepNumber, {
      getValues: () => ({ [schemaKey]: form.getValues()[schemaKey] }),
      getSubmitHandler: (setValues) => () => {
        setValues({ [schemaKey]: form.getValues()[schemaKey] });
        return Promise.resolve();
      },
      getFormState: () => ({
        isSubmitting: false,
        isValid: true,
      }),
    });
  }, [stepNumber, JSON.stringify(formValues)]);

  const sectionTitle =
    schemaType === SchemaType.INPUT ? "Input Schema" : "Output Schema";
  const emptyTitle =
    schemaType === SchemaType.INPUT ? "No input schema." : "No output schema.";
  const emptyDescription =
    schemaType === SchemaType.INPUT
      ? "Add inputs manually."
      : "Add outputs manually.";

  if (currentStep !== stepNumber) return null;

  return (
    <Panel
      className={cx("panel")}
      loaded={!isLoadingWorkerVersions}
    >
      <Form
        {...form}
        className={cx("form")}
      >
        <h4 className={cx("section-title")}>{sectionTitle}</h4>
        {schemaEntries.length > 0 ? (
          <Accordion defaultActiveKeys={[]}>
            {schemaEntries.map(([propertyKey, configuration]) => (
              <AccordionPanel
                key={propertyKey}
                dataKey={`schema-${propertyKey}`}
                title={configuration.display_name}
                actionRow={
                  <>
                    <Button
                      icon="pencil-simple"
                      variant="text"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleEditProperty(propertyKey);
                      }}
                      aria-label={`Edit ${propertyKey}`}
                    />
                    <Button
                      icon="trash"
                      variant="text-error"
                      onClick={(event) => {
                        event.stopPropagation();
                        handleDeleteProperty(propertyKey);
                      }}
                      aria-label={`Delete ${propertyKey}`}
                    />
                  </>
                }
              >
                <InputOutputSchemaPropertyConfiguration
                  propertyKey={propertyKey}
                  configuration={configuration}
                  variant="shaded"
                  color="neutral"
                />
              </AccordionPanel>
            ))}
          </Accordion>
        ) : (
          <EmptyState
            title={emptyTitle}
            suggestion={emptyDescription}
          />
        )}
        <div className={cx("button-container")}>
          {latestWorkerVersion && (
            <Button
              variant="primary"
              onClick={() => setIsImportConfirmationDialogOpen(true)}
              disabled={!latestWorkerVersion}
            >
              Import from latest version
            </Button>
          )}
          <Button
            variant="primary"
            onClick={() => setAddSchemaPropertyDialogState({ mode: "add" })}
          >
            {schemaType === SchemaType.INPUT ? "Add input" : "Add output"}
          </Button>
        </div>
        <AddSchemaPropertyDialog
          key={`schema-property-dialog-${editingProperty?.propertyKey ?? "add"}`}
          isOpen={isAddSchemaPropertyDialogOpen}
          onClose={() => setAddSchemaPropertyDialogState(null)}
          onSubmit={handleAddProperty}
          existingPropertyKeys={Object.keys(schema)}
          editingProperty={editingProperty}
          isOutputSchema={schemaType === SchemaType.OUTPUT}
        />
        <Dialog
          title="Import from latest version"
          isOpen={isImportConfirmationDialogOpen}
          onClose={() => setIsImportConfirmationDialogOpen(false)}
          actionRow={[
            <Button
              key="confirm-import"
              variant="primary"
              onClick={handleImportFromLatestVersion}
            >
              Confirm
            </Button>,
          ]}
        >
          <p className={cx("import-schema-confirmation-description")}>
            This will replace your current {schemaType} schema with the schema
            from the latest worker version. Any changes you have made will be
            lost. Do you want to proceed?
          </p>
        </Dialog>
      </Form>
    </Panel>
  );
};

export default WorkerVersionInputOutputSchemaFormStep;
